import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { KeyRound, Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { dashboardApi } from '@/services/dashboardApi';

export default function DashboardResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('La contraseña debe tener al menos 8 caracteres');
    if (!/[A-Z]/.test(password)) return setError('La contraseña debe incluir al menos una mayúscula');
    if (!/[0-9]/.test(password)) return setError('La contraseña debe incluir al menos un número');
    if (password !== confirm) return setError('Las contraseñas no coinciden');
    setLoading(true);
    try {
      await dashboardApi.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/dashboard/login', { replace: true }), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#c9a84c]/40 disabled:opacity-50 placeholder:text-gray-600';

  return (
    <div className="min-h-screen bg-[#060d1b] flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6" aria-label="Volver a la web de Freedom Homes Club">
            <img src="/logo-fhc-sm.png" alt="Freedom Homes Club" className="h-16 w-auto mx-auto" />
          </Link>
          <h1 className="text-2xl font-bold text-white mb-2 tracking-tight">Crea tu nueva contraseña</h1>
          <p className="text-gray-400 text-sm">Mínimo 8 caracteres, con una mayúscula y un número.</p>
        </div>

        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-8" data-testid="reset-card">
          {!token ? (
            <div className="text-center space-y-4">
              <p className="text-sm text-gray-300">Este enlace no es válido.</p>
              <Link to="/dashboard/forgot-password" className="text-sm text-[#c9a84c] hover:underline">Pedir un enlace nuevo</Link>
            </div>
          ) : done ? (
            <div className="text-center space-y-3" data-testid="reset-done">
              <CheckCircle2 size={36} className="text-emerald-400 mx-auto" />
              <p className="text-sm text-gray-300">Contraseña actualizada. Te llevamos al acceso…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="reset-pw" className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2">Nueva contraseña</label>
                <div className="relative">
                  <input id="reset-pw" type={show ? 'text' : 'password'} autoComplete="new-password" required value={password} disabled={loading}
                    onChange={(e) => setPassword(e.target.value)} className={`${inputClass} pr-11`} data-testid="reset-password" />
                  <button type="button" onClick={() => setShow(v => !v)} aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                    {show ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="reset-pw2" className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2">Repite la contraseña</label>
                <input id="reset-pw2" type={show ? 'text' : 'password'} autoComplete="new-password" required value={confirm} disabled={loading}
                  onChange={(e) => setConfirm(e.target.value)} className={inputClass} data-testid="reset-confirm" />
              </div>
              {error && (
                <p className="text-red-400 text-sm" role="alert">
                  {error}{' '}
                  {/enlace/i.test(error) && <Link to="/dashboard/forgot-password" className="underline">Pedir uno nuevo</Link>}
                </p>
              )}
              <button type="submit" disabled={loading} className="w-full bg-[#c9a84c] hover:bg-[#b8973f] text-[#0a1628] font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50" data-testid="reset-submit">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={15} />}
                {loading ? 'Guardando…' : 'Guardar contraseña'}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
